"use client";

import { CompactNumber } from "@/shared/ui/CompactNumber";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@tanstack/react-router";
import {
    ClockIcon,
    DollarSignIcon,
    GaugeIcon,
    ShoppingBagIcon,
    WalletIcon,
} from "lucide-react";

export type DashboardKPICardsProps = {
  todaySalesLak?: number;
  todayOrdersCount?: number;
  totalWalletLak?: number;
  pendingOrdersCount?: number;
  lowStockItemCount?: number;
  showLowStock?: boolean;
  isLoading?: boolean;
};

export function DashboardKPICards({
  todaySalesLak,
  todayOrdersCount,
  totalWalletLak,
  pendingOrdersCount,
  lowStockItemCount,
  showLowStock = false,
  isLoading,
}: DashboardKPICardsProps) {
  if (isLoading) {
    const skeletonCount = showLowStock && todaySalesLak === undefined ? 1 : 4;
    return (
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: skeletonCount }, (_, i) => i + 1).map((i) => (
          <Card key={i} className="rounded-xl border bg-card">
            <CardContent className="">
              <div className="h-10 animate-pulse rounded bg-muted" />
              <div className="mt-2 h-6 w-16 animate-pulse rounded bg-muted" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (showLowStock && todaySalesLak === undefined && lowStockItemCount !== undefined) {
    return (
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Link to="/app/reports/inventory/low-stock" className="block">
          <Card className="rounded-xl border border-amber-500/20 bg-card transition-colors hover:bg-muted/30">
            <CardContent className="flex items-center gap-4">
              <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-amber-500/10">
                <GaugeIcon className="size-5 text-amber-600" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-muted-foreground text-sm">
                  ຕ່ຳກວ່າເກັນໄຂ (policy)
                </p>
                <p className="font-semibold text-xl tabular-nums">
                  <CompactNumber value={lowStockItemCount} />
                </p>
              </div>
            </CardContent>
          </Card>
        </Link>
      </div>
    );
  }

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {/* Today Sales */}
      <Card className="rounded-xl border border-green-500/20 bg-card">
        <CardContent className="flex items-center gap-4">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-green-500/10">
            <DollarSignIcon className="size-5 text-green-600" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-muted-foreground text-sm">ຍອດຂາຍມື້ນີ້</p>
            <p className="font-semibold text-xl tabular-nums">
              {todaySalesLak !== undefined ? (
                <CompactNumber value={todaySalesLak} suffix=" LAK" />
              ) : (
                "–"
              )}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Today Orders */}
      <Card className="rounded-xl border border-blue-500/20 bg-card">
        <CardContent className="flex items-center gap-4">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-blue-500/10">
            <ShoppingBagIcon className="size-5 text-blue-600" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-muted-foreground text-sm">ຈຳນວນອໍເດີມື້ນີ້</p>
            <p className="font-semibold text-xl tabular-nums">
              {todayOrdersCount !== undefined ? (
                <CompactNumber value={todayOrdersCount} />
              ) : (
                "–"
              )}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Total Wallet */}
      <Card className="rounded-xl border border-purple-500/20 bg-card">
        <CardContent className="flex items-center gap-4">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-purple-500/10">
            <WalletIcon className="size-5 text-purple-600" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-muted-foreground text-sm">ຍອດລວມ Wallet</p>
            <p className="font-semibold text-xl tabular-nums">
              {totalWalletLak !== undefined ? (
                <CompactNumber value={totalWalletLak} suffix=" LAK" />
              ) : (
                "–"
              )}
            </p>
          </div>
        </CardContent>
      </Card>

      {showLowStock && lowStockItemCount !== undefined && (
        <Link to="/app/reports/inventory/low-stock" className="block">
          <Card className="rounded-xl border border-amber-500/20 bg-card transition-colors hover:bg-muted/30">
            <CardContent className="flex items-center gap-4">
              <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-amber-500/10">
                <GaugeIcon className="size-5 text-amber-600" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-muted-foreground text-sm">
                  ຕ່ຳກວ່າເກັນໄຂ (policy)
                </p>
                <p className="font-semibold text-xl tabular-nums">
                  <CompactNumber value={lowStockItemCount} />
                </p>
              </div>
            </CardContent>
          </Card>
        </Link>
      )}

      {/* Pending Orders */}
      {pendingOrdersCount !== undefined && pendingOrdersCount > 0 && (
        <Card className="rounded-xl border border-orange-500/20 bg-card">
          <CardContent className="flex items-center gap-4">
            <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-orange-500/10">
              <ClockIcon className="size-5 text-orange-600" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-muted-foreground text-sm">ອໍເດີລໍຖ້າອນຸມັດ</p>
              <p className="font-semibold text-xl tabular-nums">
                <CompactNumber value={pendingOrdersCount} />
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
